/** Per-install `localStorage` key. Namespaced under `client-node.` like the theme
 * (`nodeos-theme` predates that convention) and the rest of this adapter family. */
const STORAGE_KEY = "client-node.home-node";
/**
 * The browser adapter behind `HomeNodeCandidateStorePort` (lan-direct-pairing plan, C3) —
 * `localStorage`-backed, one JSON row per install. Mirrors
 * `BrowserThemeEnvironment`'s try/catch-swallow shape exactly (`theme/browser-theme-
 * environment.ts`): a private-browsing or quota failure degrades to "nothing persisted"
 * rather than throwing, since losing this candidate only means LAN discovery runs again on
 * the next launch — never a hard failure.
 */
export class BrowserHomeNodeCandidateStore {
    #storage;
    constructor(options = {}) {
        this.#storage = options.storage ?? safeGlobalLocalStorage();
    }
    read() {
        try {
            const raw = this.#storage?.getItem(STORAGE_KEY);
            return raw === null || raw === undefined ? undefined : parseStoredCandidate(raw);
        }
        catch {
            // Storage inaccessible (private browsing, disabled) — same fallback as
            // `BrowserThemeEnvironment.getStoredTheme`.
            return undefined;
        }
    }
    write(candidate) {
        try {
            this.#storage?.setItem(STORAGE_KEY, JSON.stringify(candidate));
        }
        catch {
            // Storage unavailable (private browsing, quota) — the caller's reload simply re-runs
            // discovery next time, matching `BrowserThemeEnvironment.setStoredTheme`.
        }
    }
    clear() {
        try {
            this.#storage?.removeItem(STORAGE_KEY);
        }
        catch {
            // Ignored — see `write()`.
        }
    }
}
export function createBrowserHomeNodeCandidateStore(options = {}) {
    return new BrowserHomeNodeCandidateStore(options);
}
function parseStoredCandidate(raw) {
    let value;
    try {
        value = JSON.parse(raw);
    }
    catch {
        return undefined;
    }
    if (typeof value !== "object" || value === null)
        return undefined;
    const record = value;
    const { address, id } = record;
    if (typeof address !== "string" || address.length === 0)
        return undefined;
    if (typeof id !== "string" || id.length === 0)
        return undefined;
    return { address, id };
}
function safeGlobalLocalStorage() {
    try {
        return globalThis.localStorage;
    }
    catch {
        // Accessing the property itself can throw in some locked-down embedders.
        return undefined;
    }
}
//# sourceMappingURL=browser-home-node-candidate-store.js.map